import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import type { LeaveSummaryData } from "@/lib/api";

const LEAVE_COLORS: Record<string, string> = {
  annual: "#3b82f6",
  vacation: "#3b82f6",
  sick: "#ef4444",
  parental: "#10b981",
  maternity: "#10b981",
  paternity: "#06b6d4",
  unpaid: "#f59e0b",
  compassionate: "#8b5cf6",
  other: "#ec4899",
};

function getLeaveColor(type: string, idx: number): string {
  const fallbacks = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#ef4444", "#14b8a6"];
  return LEAVE_COLORS[type.toLowerCase()] ?? fallbacks[idx % fallbacks.length];
}

interface Props {
  data: LeaveSummaryData | undefined;
  isLoading?: boolean;
}

export function LeaveAnalytics({ data, isLoading }: Props) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("analytics.leaveAnalytics")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    );
  }

  const leaveTypes = data?.leaveTypes ?? [];
  const chartData = Object.entries(data?.byMonth ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, types]) => ({ month, ...types }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("analytics.leaveAnalytics")}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.noData")}</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                width={40}
                label={{ value: "Days", angle: -90, position: "insideLeft", fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: 13,
                }}
              />
              <Legend />
              {leaveTypes.map((type, idx) => (
                <Bar
                  key={type}
                  dataKey={type}
                  stackId="leave"
                  fill={getLeaveColor(type, idx)}
                  name={type.charAt(0).toUpperCase() + type.slice(1)}
                  radius={idx === leaveTypes.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
